import { useState, useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Users, Gift, RefreshCw, Trophy, Award, Download } from 'lucide-react';
import { format } from 'date-fns';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

interface ReferralRow {
  id: string;
  referrer_id: string;
  referred_user_id: string | null;
  referred_email: string;
  referred_name: string;
  status: string;
  bonus_granted: boolean;
  qualified_at: string | null;
  created_at: string;
  referrer_name?: string;
  referred_profile_name?: string;
}

interface RewardRow {
  id: string;
  user_id: string;
  tier_name: string;
  referral_count_at_award: number;
  reward_credits: number;
  awarded_at: string;
  user_name?: string;
}

interface LeaderboardRow {
  user_id: string;
  full_name: string | null;
  referral_code: string | null;
  tier1_free: number;
  tier1_paid: number;
  tier1_total: number;
  tier2_free: number;
  tier2_paid: number;
  tier2_total: number;
  combined_free: number;
  combined_paid: number;
  combined_total: number;
}

type SortField = 'combined_total' | 'combined_paid' | 'combined_free' | 'tier2_total';

export default function AdminReferrals() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [referrals, setReferrals] = useState<ReferralRow[]>([]);
  const [rewards, setRewards] = useState<RewardRow[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortField>('combined_total');
  const [activeTab, setActiveTab] = useState<'challenge' | 'referrals' | 'rewards'>('challenge');

  useEffect(() => {
    if (!user) return;
    supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }).then(({ data }) => {
      setIsAdmin(!!data);
    });
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [refsRes, rewsRes, leaderRes] = await Promise.all([
        supabase.from('referrals').select('*').order('created_at', { ascending: false }).limit(200),
        supabase.from('referral_rewards').select('*').order('awarded_at', { ascending: false }).limit(200),
        supabase.rpc('admin_get_referral_leaderboard'),
      ]);

      // Get unique user IDs for name lookup
      const userIds = new Set<string>();
      (refsRes.data || []).forEach((r: any) => {
        userIds.add(r.referrer_id);
        if (r.referred_user_id) userIds.add(r.referred_user_id);
      });
      (rewsRes.data || []).forEach((r: any) => userIds.add(r.user_id));

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', Array.from(userIds));

      const nameMap: Record<string, string> = {};
      (profiles || []).forEach((p: any) => {
        nameMap[p.user_id] = p.full_name || 'Unknown';
      });

      setReferrals(
        (refsRes.data || []).map((r: any) => ({
          ...r,
          referrer_name: nameMap[r.referrer_id] || r.referrer_id.slice(0, 8),
          referred_profile_name: r.referred_user_id ? nameMap[r.referred_user_id] || r.referred_name : r.referred_name,
        }))
      );

      setRewards(
        (rewsRes.data || []).map((r: any) => ({
          ...r,
          user_name: nameMap[r.user_id] || r.user_id.slice(0, 8),
        }))
      );

      setLeaderboard((leaderRes.data as unknown as LeaderboardRow[]) || []);
    } catch (e) {
      toast.error('Failed to load referral data');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin]);

  if (isAdmin === false) return <Navigate to="/dashboard" replace />;

  const statusColor = (s: string) => {
    switch (s) {
      case 'qualified': case 'completed': return 'default';
      case 'signed_up': return 'secondary';
      case 'pending': return 'outline';
      default: return 'destructive';
    }
  };

  const tierLabel = (t: string) => t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Stats
  const totalReferrals = referrals.length;
  const qualifiedCount = referrals.filter(r => r.status === 'qualified' || r.status === 'completed').length;
  const totalCreditsAwarded = rewards.reduce((s, r) => s + r.reward_credits, 0);

  // Sorted leaderboard
  const sortedLeaderboard = [...leaderboard].sort((a, b) => b[sortBy] - a[sortBy]);

  const handleExportCSV = () => {
    const headers = ['Name', 'Referral Code', 'T1 Free', 'T1 Paid', 'T1 Total', 'T2 Free', 'T2 Paid', 'T2 Total', 'Combined Free', 'Combined Paid', 'Combined Total'];
    const rows = sortedLeaderboard.map(r => [
      r.full_name || '', r.referral_code || '',
      r.tier1_free, r.tier1_paid, r.tier1_total,
      r.tier2_free, r.tier2_paid, r.tier2_total,
      r.combined_free, r.combined_paid, r.combined_total,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `referral-challenge-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">Referral Debug</h1>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold text-foreground">{totalReferrals}</p>
                <p className="text-xs text-muted-foreground">Total Referrals</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Gift className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold text-foreground">{qualifiedCount}</p>
                <p className="text-xs text-muted-foreground">Qualified (Paid)</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Trophy className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold text-foreground">{totalCreditsAwarded}</p>
                <p className="text-xs text-muted-foreground">Credits Awarded</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6">
          {(['challenge', 'referrals', 'rewards'] as const).map(tab => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab)}
              className="capitalize"
            >
              {tab === 'challenge' && <Award className="h-4 w-4 mr-1.5" />}
              {tab === 'referrals' && <Users className="h-4 w-4 mr-1.5" />}
              {tab === 'rewards' && <Trophy className="h-4 w-4 mr-1.5" />}
              {tab === 'challenge' ? 'Challenge Leaderboard' : tab === 'referrals' ? 'Referral Relationships' : 'Tier Rewards'}
            </Button>
          ))}
        </div>

        {/* Challenge Leaderboard Tab */}
        {activeTab === 'challenge' && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-primary" />
                  Referral Challenge Leaderboard
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortField)}>
                    <SelectTrigger className="w-[180px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="combined_total">Most Total Referrals</SelectItem>
                      <SelectItem value="combined_paid">Most Paid Referrals</SelectItem>
                      <SelectItem value="combined_free">Most Free Referrals</SelectItem>
                      <SelectItem value="tier2_total">Highest Tier 2 Activity</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={handleExportCSV} className="h-8 gap-1.5">
                    <Download className="h-3.5 w-3.5" />
                    CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : sortedLeaderboard.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No referral activity yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead className="text-center" colSpan={3}>Tier 1</TableHead>
                        <TableHead className="text-center" colSpan={3}>Tier 2</TableHead>
                        <TableHead className="text-center" colSpan={3}>Combined</TableHead>
                      </TableRow>
                      <TableRow>
                        <TableHead></TableHead>
                        <TableHead></TableHead>
                        <TableHead></TableHead>
                        <TableHead className="text-center text-xs">Free</TableHead>
                        <TableHead className="text-center text-xs">Paid</TableHead>
                        <TableHead className="text-center text-xs">Total</TableHead>
                        <TableHead className="text-center text-xs">Free</TableHead>
                        <TableHead className="text-center text-xs">Paid</TableHead>
                        <TableHead className="text-center text-xs">Total</TableHead>
                        <TableHead className="text-center text-xs">Free</TableHead>
                        <TableHead className="text-center text-xs">Paid</TableHead>
                        <TableHead className="text-center text-xs font-bold">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedLeaderboard.map((r, i) => (
                        <TableRow key={r.user_id}>
                          <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                          <TableCell className="font-medium text-sm">{r.full_name || '—'}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">{r.referral_code || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{r.tier1_free}</TableCell>
                          <TableCell className="text-center text-sm text-primary font-semibold">{r.tier1_paid}</TableCell>
                          <TableCell className="text-center text-sm font-bold">{r.tier1_total}</TableCell>
                          <TableCell className="text-center text-sm">{r.tier2_free}</TableCell>
                          <TableCell className="text-center text-sm text-primary font-semibold">{r.tier2_paid}</TableCell>
                          <TableCell className="text-center text-sm font-bold">{r.tier2_total}</TableCell>
                          <TableCell className="text-center text-sm">{r.combined_free}</TableCell>
                          <TableCell className="text-center text-sm text-primary font-semibold">{r.combined_paid}</TableCell>
                          <TableCell className="text-center text-sm font-bold text-foreground">{r.combined_total}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Referrals Table */}
        {activeTab === 'referrals' && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Referral Relationships</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : referrals.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No referrals yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Referrer</TableHead>
                        <TableHead>Referred</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Qualified At</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {referrals.map(r => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium text-sm">{r.referrer_name}</TableCell>
                          <TableCell className="text-sm">{r.referred_profile_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground font-mono">{r.referred_email}</TableCell>
                          <TableCell>
                            <Badge variant={statusColor(r.status) as any}>{r.status}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.qualified_at ? format(new Date(r.qualified_at), 'MMM d, yyyy HH:mm') : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {format(new Date(r.created_at), 'MMM d, yyyy')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tier Rewards Table */}
        {activeTab === 'rewards' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tier Rewards Awarded</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : rewards.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No tier rewards awarded yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead>Credits</TableHead>
                        <TableHead>Referrals at Award</TableHead>
                        <TableHead>Awarded At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rewards.map(r => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium text-sm">{r.user_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{tierLabel(r.tier_name)}</Badge>
                          </TableCell>
                          <TableCell className="font-bold text-primary">+{r.reward_credits}</TableCell>
                          <TableCell className="text-sm">{r.referral_count_at_award}</TableCell>
                          <TableCell className="text-xs">
                            {format(new Date(r.awarded_at), 'MMM d, yyyy HH:mm')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
}
